import React from "react";
import useFormattingUtils from "../../../../../../context/useFormattingUtils";

const FONT_SIZES = { small: "10px", medium: "12px", large: "14px" };
const FONT_FAMILIES = {
  default: "'Courier New', monospace",
  serif: "Georgia, 'Times New Roman', serif",
  modern: "'Segoe UI', 'Poppins', system-ui, sans-serif",
  monospace: "'Courier New', monospace",
};

// Settings-driven thermal receipt (58mm / 80mm) printable invoice.
const ThermalInvoiceTemplate = ({ layout = {}, invoice = {}, storeInfo = {} }) => {
  const { formatCurrency, formatDate } = useFormattingUtils();
  const width = layout.paper_width === "58mm" ? "58mm" : "80mm";
  const fontSize = FONT_SIZES[layout.font_size] || FONT_SIZES.medium;
  const fontFamily = FONT_FAMILIES[layout.font_family] || FONT_FAMILIES.default;
  const brandColor = layout.brand_color || "#000000";
  const items = invoice.items || [];
  const totals = invoice.totals || {};
  // Business identity: an explicit override in the layout settings always
  // wins over whatever the Store record has (which may be blank or stale).
  const businessName = layout.business_name || storeInfo.name || "Store Name";
  const businessAddress = layout.business_address || storeInfo.address || "";
  const businessContact = layout.business_contact || storeInfo.contact || "";
  // Always follow the store's configured currency/date format, matching every other invoice screen.
  const money = (amount) => formatCurrency(amount || 0);

  return (
    <div className="thermal-invoice-wrap" style={{ width, fontSize }}>
      <style>{`
        .thermal-invoice-wrap { --brand: ${brandColor}; margin: 0 auto; padding: 8px; background: #fff; color: #000; font-family: ${fontFamily}; }
        .thermal-invoice-wrap .th-center { text-align: center; }
        .thermal-invoice-wrap .th-header-image { max-width: 100%; max-height: 80px; object-fit: contain; margin: 0 auto 6px; display: block; }
        .thermal-invoice-wrap .th-footer-image { max-width: 100%; max-height: 80px; object-fit: contain; margin: 8px auto 0; display: block; }
        .thermal-invoice-wrap .th-store-name { font-weight: bold; font-size: 1.3em; margin: 0 0 2px; color: var(--brand); }
        .thermal-invoice-wrap .th-line { border-top: 1px dashed #000; margin: 6px 0; }
        .thermal-invoice-wrap table { width: 100%; border-collapse: collapse; }
        .thermal-invoice-wrap th, .thermal-invoice-wrap td { text-align: left; padding: 2px 0; font-size: 0.95em; }
        .thermal-invoice-wrap .th-row { display: flex; justify-content: space-between; }
        .thermal-invoice-wrap .th-grand { font-weight: bold; }
        @media print {
          @page { size: ${width} auto; margin: 0; }
          body * { visibility: hidden; }
          /* Scope visibility to the dedicated print mount only -- see the
             matching comment in A4InvoiceTemplate.js for why a bare class
             selector here duplicates the receipt across two pages. */
          .__invoice_print_root, .__invoice_print_root * { visibility: visible; }
          .__invoice_print_root .thermal-invoice-wrap { position: absolute; left: 0; top: 0; }
        }
      `}</style>

      {layout.header_image && <img src={layout.header_image} alt="" className="th-header-image" />}

      <div className="th-center">
        {layout.show_logo && storeInfo.logo && <img src={storeInfo.logo} alt="logo" style={{ maxHeight: 40 }} />}
        <p className="th-store-name">{businessName}</p>
        {layout.show_store_address && (businessAddress || businessContact) && (
          <>
            <p style={{ margin: "2px 0" }}>{businessAddress}</p>
            <p style={{ margin: "2px 0" }}>{businessContact}</p>
          </>
        )}
      </div>

      <div className="th-line" />
      <div>Date: {formatDate(invoice.date) || invoice.date}</div>
      <div>Bill No: {invoice.invoiceNo}</div>
      {layout.show_customer_block && invoice.customer?.name && (
        <div>Customer: {invoice.customer.name}</div>
      )}
      {layout.header_note && <div style={{ marginTop: 4 }}>{layout.header_note}</div>}
      <div className="th-line" />

      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Rate</th>
            <th>Amt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx}>
              <td>{item.name}</td>
              <td>{item.qty}</td>
              <td>{money(item.rate)}</td>
              <td>{money(item.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="th-line" />
      <div className="th-row"><span>Total Qty</span><span>{totals.totalQty}</span></div>
      {layout.show_tax_breakdown && (
        <div className="th-row"><span>Tax</span><span>{money(totals.taxAmount)}</span></div>
      )}
      <div className="th-row"><span>Round Off</span><span>{money(totals.roundOff)}</span></div>
      <div className="th-row th-grand"><span>Payable</span><span>{money(totals.payableAmount)}</span></div>
      <div className="th-row"><span>Payment</span><span>{totals.payMethod}</span></div>

      {layout.show_barcode && (
        <div className="th-center" style={{ marginTop: 8, fontFamily: "monospace", letterSpacing: 2 }}>
          *{invoice.invoiceNo}*
        </div>
      )}

      {layout.show_notes_footer && layout.footer_note && (
        <div className="th-center" style={{ marginTop: 8 }}>{layout.footer_note}</div>
      )}

      {layout.footer_image && <img src={layout.footer_image} alt="" className="th-footer-image" />}
    </div>
  );
};

export default ThermalInvoiceTemplate;
